import type { LabDescriptor } from '../../../domain';

export type AppSwitcherGroup = 'lab' | 'surface';

export interface AppSwitcherItem {
  /** Stable key — the lab id for labs, a route-ish slug for surfaces. */
  id: string;
  label: string;
  /** Where clicking navigates. */
  to: string;
  group: AppSwitcherGroup;
  /** Longer text for the tile's tooltip. */
  description?: string;
}

/** Top-level surfaces that aren't labs but belong in the switcher. */
const SURFACES: readonly AppSwitcherItem[] = [
  {
    id: 'assets',
    label: 'Asset Gallery',
    to: '/assets',
    group: 'surface',
    description: 'Every image and document you have created',
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    to: '/dashboard',
    group: 'surface',
    description: 'Your landing page — recent work and launchers',
  },
  {
    id: 'settings-keys',
    label: 'API keys',
    to: '/settings/keys',
    group: 'surface',
    description: 'Use your own provider keys instead of the platform’s',
  },
];

/**
 * Builds the app-switcher grid.
 *
 * Labs come from the backend catalog rather than a literal list, so the switcher
 * inherits the SMA-13 feature gate for free: a lab the backend omits from
 * `/api/v3/labs` simply isn't offered here either, and can't be reached by
 * clicking through. Surfaces are always present — they aren't gated.
 *
 * Returns labs first, then surfaces, which is the order the grid renders in.
 * While the catalog is still loading (`labs` undefined) only the surfaces come
 * back, so the popover has something rather than flashing empty.
 */
export function buildAppSwitcherItems(
  labs: LabDescriptor[] | undefined,
): AppSwitcherItem[] {
  const labItems: AppSwitcherItem[] = (labs ?? []).map(lab => ({
    id: lab.id,
    label: lab.name || lab.id,
    to: `/labs/${lab.id}`,
    group: 'lab' as const,
    description: lab.description ?? undefined,
  }));

  return [...labItems, ...SURFACES];
}

/**
 * Which switcher entry represents where the user currently is, so the grid can
 * mark it. Lab routes win when a `labId` is active; otherwise the pathname is
 * matched against the surfaces (`/collections/…` counts as the gallery, since
 * that's the same surface deep-linked).
 */
export function activeAppId(
  pathname: string,
  labId?: string,
): string | undefined {
  if (labId) return labId;
  if (pathname.startsWith('/assets') || pathname.startsWith('/collections')) return 'assets';
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  if (pathname.startsWith('/settings/keys')) return 'settings-keys';
  return undefined;
}
